import { motion } from "framer-motion";
import type { ReactNode } from "react";

type HeroSectionProps = {
  title?: ReactNode;
  description?: string;
  ctaLabel?: string;
  ctaTarget?: string;
  supportingText?: string;
  className?: string;
};

const HeroSection = ({ title, description, ctaLabel, ctaTarget = "contact", supportingText, className = "" }: HeroSectionProps) => {
  const scrollToTarget = () => {
    document.getElementById(ctaTarget)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      className={`hero-orange-gradient relative z-[1] flex min-h-[100svh] items-center overflow-hidden px-4 py-24 text-white sm:px-6 sm:py-28 lg:aspect-[16/9] lg:min-h-0 lg:px-8 lg:py-24 ${className}`}
    >
      <div className="hero-orange-gradient__texture" aria-hidden="true" />
      <div className="relative z-10 mx-auto w-full max-w-[1220px]">
        <motion.h1
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className={`max-w-[1120px] font-heading font-medium leading-[1.02] tracking-normal ${
            title
              ? "text-[clamp(3rem,7.2vw,7.2rem)]"
              : "text-[clamp(1.8rem,9vw,2.3rem)] sm:text-[clamp(3rem,7.2vw,7.2rem)]"
          }`}
        >
          {title ?? (
            <>
              <span className="whitespace-nowrap">AI-трансформация</span>
              <br />
              для роста бизнеса
            </>
          )}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-[760px] font-body text-lg leading-[1.38] text-white/90 sm:text-xl lg:mt-10 lg:text-[26px]"
        >
          {description ?? "Помогаем находить и выбирать ИИ-инициативы, проверять их с минимальными затратами и масштабировать успешные кейсы до результата в деньгах."}
        </motion.p>

        <motion.button
          type="button"
          onClick={scrollToTarget}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ y: -2, scale: 1.015 }}
          whileTap={{ scale: 0.985 }}
          className="mt-10 inline-flex h-[58px] min-w-[250px] items-center justify-center rounded-full bg-white px-8 font-body text-base font-medium text-accent shadow-[0_16px_30px_-16px_rgba(42,16,8,0.45)] transition-shadow hover:shadow-[0_20px_36px_-16px_rgba(42,16,8,0.62)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-accent sm:mt-14 sm:h-[64px] sm:min-w-[320px] sm:text-xl"
        >
          {ctaLabel ?? "Начать трансформацию"}
        </motion.button>

        {supportingText && (
          <p className="mt-6 max-w-[520px] font-body text-sm leading-relaxed text-white/75">{supportingText}</p>
        )}
      </div>
    </section>
  );
};

export default HeroSection;
